import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useResourcesHandlers } from '@lp/features/RoutePlanner/hooks/useResourcesHandlers';
import { useSetBenchmarkToCase } from '@lp/features/RoutePlanner/hooks/useSetBenchmarkToCase';
import { useRouteBuilderState } from '@lp/features/RoutePlanner/store';
import {
  getAircraftsToRemove,
  getHubsToRemove,
  getLegsToRemove,
  getResourcesOnRoute,
  getResourcesToRemoveIds,
} from '@lp/features/RoutePlanner/utils';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { useUpdateRouteStatus } from '@/hooks/useUpdateRouteStatus';
import { useQuoteSendToTXCenter } from '@/queries/quotes';
import { OperationStatusResponse } from '@/api/apis/orchestrator';
import { CaseResourceType, CaseStatus, OrganType, ResourceLogisticsStatus, RoutePlanStatus } from '@/api/apis/case';
import {
  useCaseResourcesDataQuery,
  useApproveQuoteByTxMutation,
  useCaseByIdQuery,
  useGroundApprove,
  useCaseReviewsDataQuery,
  useRoutePlanningDone,
  useSetRoutePlanning,
} from '@/queries/case';
import { getIsEuropeanRegion, isAllowedStatus, sleep } from '@/utils/helpers';
import { useSendSuggestedRoutesMetrics, useRemoveRoute, useRemoveRouteHubs } from '@/queries/route-planner';
import { HubTypeEnum, RouteTypeEnum } from '@/api/apis/route';

export const useLogisticsActions = () => {
  const [isLoading, setLoading] = useState(false);
  const setActionLoading = useRouteBuilderState.use.setActionLoading();
  const {
    caseId,
    organType,
    donorType,
    opoRequest,
    isExpeditedCase,
    donorHospitalDetails: donorHospital,
    txCenterDetails: txCenter,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    status,
    routePlanningStatus,
    serviceRegionId,
    useFixedPriceFlow,
    resources,
  } = useCaseDetailsWithReviews();
  const { id, version } = useLogisticsData();
  const mainRoute = useRouteDetails(RouteTypeEnum.Main);
  const dryRunRoute = useRouteDetails(RouteTypeEnum.DryRun);
  const { ClinicalSpecialist: specialists, OCSConsole: devices, Surgeon: surgeons } = useRouteResources();
  const { isSubmitting, updateStatus } = useUpdateRouteStatus();
  const { mutateAsync: sendQuoteToTxReview } = useQuoteSendToTXCenter();
  const { handleResourceAssignment, handleBookResources } = useResourcesHandlers({ id: caseId });
  const { mutateAsync: approveGround } = useGroundApprove();
  const { mutateAsync: setRoutePlanning } = useSetRoutePlanning();
  const { mutateAsync: setRoutePlanningDone } = useRoutePlanningDone();
  const { mutateAsync: approveQuoteByTXC } = useApproveQuoteByTxMutation();
  const { mutateAsync: sendSuggestedRoutesMetrics } = useSendSuggestedRoutesMetrics();
  const { mutateAsync: removeRoutes } = useRemoveRoute();
  const { mutateAsync: removeRouteHubs } = useRemoveRouteHubs();
  const { refetch: refetchCase } = useCaseByIdQuery(caseId);
  const { refetch: refetchCaseResourcesData } = useCaseResourcesDataQuery(caseId);
  const { refetch: refetchCaseReviewsData } = useCaseReviewsDataQuery(caseId);

  const aircrafts = useMemo(() => resources.filter((r) => r.resourceType === CaseResourceType.Aircraft), [resources]);

  // Kidney benchmark generation is unsupported by mission-planner (mirrors SuggestedRoutesHeader's isKidneyCase guard).
  const isKidneyCase = organType === OrganType.Kidney;

  const generateBenchmark = useSetBenchmarkToCase({
    caseId,
    donorHospital: donorHospital!,
    txCenter: txCenter!,
    donorType,
    organType,
    donorHospitalTime,
    donorHospitalDate,
    donorHospitalTimeZone,
  });

  const handleSendQuoteToTxReview = useCallback(async () => {
    try {
      setLoading(true);

      await sendQuoteToTxReview({ id: caseId });
    } catch (e) {
      const error = e as OperationStatusResponse;

      toast.error(error.faultedMessage);
    } finally {
      await refetchCase();
      await refetchCaseResourcesData();
      setLoading(false);
    }
  }, [caseId, sendQuoteToTxReview, refetchCase, refetchCaseResourcesData]);

  const handleCompleteRoute = useCallback(async () => {
    try {
      setLoading(true);

      await sendSuggestedRoutesMetrics({ caseId }).catch(() => {});
      await setRoutePlanningDone({ caseId });
    } catch (e) {
      const error = e as OperationStatusResponse;

      toast.error(error.faultedMessage);
    } finally {
      await refetchCase();
      await refetchCaseResourcesData();
      setLoading(false);
    }
  }, [caseId, setRoutePlanningDone, refetchCase, refetchCaseResourcesData, sendSuggestedRoutesMetrics]);

  const bookResources = useCallback(async () => {
    try {
      setLoading(true);

      await handleBookResources();
    } catch (e) {
      const error = e as OperationStatusResponse;

      toast.error(error.faultedMessage);
    } finally {
      await refetchCase();
      await refetchCaseResourcesData();
      setLoading(false);
    }
  }, [handleBookResources, refetchCase, refetchCaseResourcesData]);

  const handleSendQuoteInternal = useCallback(
    async (comment?: string) => {
      try {
        setLoading(true);

        if (!getIsEuropeanRegion(serviceRegionId) && !isKidneyCase) {
          await sendSuggestedRoutesMetrics({ caseId }).catch(() => {});
          await generateBenchmark();
          await sleep(2000);
        }

        await updateStatus(caseId, RoutePlanStatus.RoutePlanningStart, comment, true);
      } catch (e) {
        const error = e as OperationStatusResponse & Error;

        toast.error(error.faultedMessage || error?.message || 'Something went wrong. Try again.');
      } finally {
        await refetchCase();
        await refetchCaseResourcesData();
        setLoading(false);
      }
    },
    [
      serviceRegionId,
      isKidneyCase,
      updateStatus,
      caseId,
      sendSuggestedRoutesMetrics,
      generateBenchmark,
      refetchCase,
      refetchCaseResourcesData,
    ],
  );

  const sendToAviationApprove = useCallback(
    async (comment?: string) => {
      const isCaseWithoutTx = (opoRequest || !!isExpeditedCase) && !txCenter;
      const txWithQuote = !!txCenter && !useFixedPriceFlow;

      try {
        setLoading(true);

        await sendSuggestedRoutesMetrics({ caseId }).catch(() => {});
        if ((txWithQuote || !isCaseWithoutTx) && !isKidneyCase) {
          await generateBenchmark();
          await sleep(2000);
        }

        await updateStatus(caseId, RoutePlanStatus.RoutePlanningStart, comment);
      } catch (e) {
        const error = e as OperationStatusResponse & Error;

        toast.error(error.faultedMessage || error?.message || 'Something went wrong. Try again.');
      } finally {
        await refetchCase();
        await refetchCaseResourcesData();
        setLoading(false);
      }
    },
    [
      opoRequest,
      isExpeditedCase,
      txCenter,
      isKidneyCase,
      updateStatus,
      caseId,
      generateBenchmark,
      refetchCase,
      refetchCaseResourcesData,
      useFixedPriceFlow,
      sendSuggestedRoutesMetrics,
    ],
  );

  const handleAviationApprove = useCallback(async () => {
    try {
      setLoading(true);

      await updateStatus(caseId, RoutePlanStatus.WaitingForAviationApproval);
    } catch (e) {
      const error = e as OperationStatusResponse;

      toast.error(error.faultedMessage);
    } finally {
      await sleep(1000);
      await refetchCase();
      await refetchCaseResourcesData();
      setLoading(false);
    }
  }, [updateStatus, caseId, refetchCase, refetchCaseResourcesData]);

  const handleGroundApprove = useCallback(async () => {
    try {
      setLoading(true);

      await approveGround({ caseId });
    } catch (e) {
      const error = e as OperationStatusResponse;

      toast.error(error.faultedMessage);
    } finally {
      await refetchCase();
      await refetchCaseResourcesData();
      await refetchCaseReviewsData();
      setLoading(false);
    }
  }, [approveGround, caseId, refetchCase, refetchCaseResourcesData, refetchCaseReviewsData]);

  const handleApproveQuoteByTXC = useCallback(async () => {
    try {
      setLoading(true);

      await approveQuoteByTXC({ caseId });
    } catch (e) {
      const error = e as OperationStatusResponse;

      toast.error(error.faultedMessage);
    } finally {
      await refetchCase();
      await refetchCaseResourcesData();
      setLoading(false);
    }
  }, [approveQuoteByTXC, caseId, refetchCaseResourcesData, refetchCase]);

  const clearUnfinishedLegs = useCallback(async (): Promise<string[]> => {
    const isDryRunReadOnly = isAllowedStatus({
      statusFrom: CaseStatus.DeployedToTXCenter,
      currentStatus: status,
    });

    const mainRouteLegs = [...mainRoute.legs, ...mainRoute.accommodationLegs];
    const dryRunRouteLegs = [...dryRunRoute.legs, ...dryRunRoute.accommodationLegs];

    const mainLegsToRemove = getLegsToRemove(mainRouteLegs, status);
    const dryRunLegsToRemove = getLegsToRemove(dryRunRouteLegs, status, isDryRunReadOnly);
    const removedLegsSet = new Set([...mainLegsToRemove, ...dryRunLegsToRemove]);

    const mainHubsToRemove = getHubsToRemove(mainRoute.legs, removedLegsSet, [
      ...(mainRoute.departureHub
        ? [{ hubType: HubTypeEnum.DepartureHub, hubId: mainRoute.departureHub.locationId }]
        : []),
      ...(mainRoute.arrivalHub ? [{ hubType: HubTypeEnum.ArrivalHub, hubId: mainRoute.arrivalHub.locationId }] : []),
    ]);
    const dryRunHubsToRemove = getHubsToRemove(dryRunRoute.legs, removedLegsSet, [
      ...(dryRunRoute.arrivalHub
        ? [{ hubType: HubTypeEnum.ArrivalHub, hubId: dryRunRoute.arrivalHub.locationId }]
        : []),
    ]);

    const hasLegsToRemove = mainLegsToRemove.length > 0 || dryRunLegsToRemove.length > 0;
    const hasHubsToRemove = mainHubsToRemove.length > 0 || dryRunHubsToRemove.length > 0;

    if (!hasLegsToRemove && !hasHubsToRemove) {
      toast.info('No unfinished legs to clear.');
      return [];
    }

    if (hasLegsToRemove) {
      const routes = [
        ...(mainLegsToRemove.length ? [{ routeId: mainRoute.id, version, legIds: mainLegsToRemove }] : []),
        ...(dryRunLegsToRemove.length ? [{ routeId: dryRunRoute.id, version, legIds: dryRunLegsToRemove }] : []),
      ];

      await removeRoutes({ logisticsId: id, routes });
    }

    if (hasHubsToRemove) {
      const hubs = [
        ...mainHubsToRemove.map((type) => ({ routeId: mainRoute.id, version, type })),
        ...dryRunHubsToRemove.map((type) => ({ routeId: dryRunRoute.id, version, type })),
      ];

      await removeRouteHubs({ logisticsId: id, hubs });
    }

    if (aircrafts?.length) {
      const allLegs = [...mainRouteLegs, ...dryRunRouteLegs];
      const aircraftsToRemove = getAircraftsToRemove(allLegs, removedLegsSet, aircrafts);

      if (aircraftsToRemove.length) {
        await handleResourceAssignment({
          data: { aircraftIds: aircraftsToRemove.map((i) => i.resourceId) },
          type: 'remove',
        });
      }
    }

    toast.success('Unfinished legs were removed');

    return mainLegsToRemove.concat(dryRunLegsToRemove);
  }, [id, mainRoute, dryRunRoute, status, version, aircrafts, removeRoutes, handleResourceAssignment, removeRouteHubs]);

  const handleClearUnfinishedLegs = useCallback(async () => {
    try {
      setActionLoading(true);
      await clearUnfinishedLegs();
    } catch (e) {
      const error = e as OperationStatusResponse;

      toast.error(error.faultedMessage);
    } finally {
      await refetchCase();
      await refetchCaseResourcesData();
      setActionLoading(false);
    }
  }, [setActionLoading, clearUnfinishedLegs, refetchCase, refetchCaseResourcesData]);

  const handleClearRouteWithResources = useCallback(async () => {
    try {
      setActionLoading(true);

      if (routePlanningStatus !== RoutePlanStatus.RoutePlanningStart) {
        await setRoutePlanning({ caseId });
      }

      const removedLegIds = await clearUnfinishedLegs();
      const resourcesOnRoute = getResourcesOnRoute(
        [...mainRoute.legs, ...mainRoute.accommodationLegs],
        [...dryRunRoute.legs, ...dryRunRoute.accommodationLegs],
        new Set(removedLegIds),
      );

      const resourcesToDelete: Record<string, string | string[]> = {};
      const employeeStatuses = [
        ResourceLogisticsStatus.Reserved,
        ResourceLogisticsStatus.BookingSent,
        ResourceLogisticsStatus.Booked,
      ];
      const deviceStatuses = [ResourceLogisticsStatus.Reserved, ResourceLogisticsStatus.Booked];

      const clinicalSpecialistIds = getResourcesToRemoveIds(specialists, employeeStatuses, resourcesOnRoute);
      const ocsConsoleIds = getResourcesToRemoveIds(devices, deviceStatuses, resourcesOnRoute);
      const surgeonIds = getResourcesToRemoveIds(surgeons, employeeStatuses, resourcesOnRoute);

      if (clinicalSpecialistIds.length) {
        resourcesToDelete.clinicalSpecialistIds = clinicalSpecialistIds;
      }

      if (ocsConsoleIds.length) {
        resourcesToDelete.ocsConsoleIds = ocsConsoleIds;
      }

      if (surgeonIds.length) {
        resourcesToDelete.surgeonIds = surgeonIds;
      }

      if (Object.keys(resourcesToDelete).length) {
        await handleResourceAssignment({ data: resourcesToDelete, type: 'remove' });
        toast.success('Resources were removed');
      }
    } catch (e) {
      const error = e as OperationStatusResponse;

      toast.error(error.faultedMessage);
    } finally {
      await refetchCase();
      await refetchCaseResourcesData();
      setActionLoading(false);
    }
  }, [
    setActionLoading,
    routePlanningStatus,
    clearUnfinishedLegs,
    mainRoute.legs,
    mainRoute.accommodationLegs,
    dryRunRoute.legs,
    dryRunRoute.accommodationLegs,
    specialists,
    devices,
    surgeons,
    setRoutePlanning,
    caseId,
    handleResourceAssignment,
    refetchCase,
    refetchCaseResourcesData,
  ]);

  return {
    sendToAviationApprove,
    handleAviationApprove,
    handleGroundApprove,
    handleCompleteRoute,
    bookResources,
    handleSendQuoteInternal,
    handleSendQuoteToTxReview,
    handleApproveQuoteByTXC,
    handleClearUnfinishedLegs,
    handleClearRouteWithResources,
    isLoading: isLoading || isSubmitting,
  };
};
